import React from 'react'
import { SubmissionProgressProps, selectSelectedCampaignSummary, selectSelectedSubmissionView } from 'state/review'
import SubmittedAtDisplay from './SubmittedAtDisplay'
import CommentAndVoteSummary from './CommentAndVoteSummary'
import Comment from './Comment'
import ReviewButtons from './ReviewButtons'
import SubmissionContent from './SubmissionContent'
import { StepState, TaskState } from 'state/tasks'
import PostTime from './PostTime'
import EditRequestHistoryButton from 'components/Submissions/Review/Submissions/EditRequestHistory'
import ViewDetailButton from './ViewDetailButton'
import { useSelector } from 'react-redux'
import { ReviewedByDisplay } from './ReviewedByDisplay'
import styled from 'particles/themes/styled'
import { SUBMISSION_TASK_STATUS_ENUM, SUBMISSION_VIEW_ENUM } from 'state/submissions'
import ResubmittedIndicator from '../ResubmittedIndicator'

interface VerifyProgressBlockProps {
    submissionProgress: SubmissionProgressProps & {
        partiposterId: string // make it required to show profile inside edit request history modal
    }
    step: StepState
    task: TaskState
}

interface ProgressBlockProps extends VerifyProgressBlockProps {
    task: TaskState
    className?: string
}

const ProgressBlock: React.FC<ProgressBlockProps> = ({ submissionProgress, step, task, className }) => {
    const selectedCampaignSummary = useSelector(selectSelectedCampaignSummary)
    const selectedSubmissionView = useSelector(selectSelectedSubmissionView)

    const hideEditRequestHistory =
        (submissionProgress?.submission_task?.status === SUBMISSION_TASK_STATUS_ENUM.PENDING ||
            selectedSubmissionView === SUBMISSION_VIEW_ENUM.COMPLETED) &&
        !submissionProgress.resubmitted

    return (
        <>
            {!hideEditRequestHistory && (
                <ResubmitContainer hasResubmitted={!!submissionProgress.resubmitted}>
                    {submissionProgress.resubmitted && <ResubmittedIndicator />}
                    <EditRequestHistoryButton
                        submissionProgressUuid={submissionProgress.uuid}
                        partiposterId={submissionProgress.partiposterId}
                    />
                </ResubmitContainer>
            )}
            <div className={className}>
                <SubmissionContent submissionProgress={submissionProgress} step={step} />
                <PostTime submissionProgress={submissionProgress} step={step} task={task} />
                <Comment submissionProgress={submissionProgress} step={step} task={task} />
                <CommentAndVoteSummary
                    submissionProgress={submissionProgress}
                    step={step}
                    className="comment-and-vote-summary"
                />
                <ReviewButtons submissionProgress={submissionProgress} />
                <ViewDetailButton submissionProgressUuid={submissionProgress.uuid} step={step} />

                {selectedCampaignSummary && (
                    <>
                        <ReviewedByDisplay
                            submissionProgress={submissionProgress}
                            country={selectedCampaignSummary.country}
                        />
                        <SubmittedAtDisplay
                            submissionProgress={submissionProgress}
                            isTaskPending={
                                submissionProgress.submission_task?.status === SUBMISSION_TASK_STATUS_ENUM.PENDING
                            }
                            country={selectedCampaignSummary.country}
                        />
                    </>
                )}
            </div>
        </>
    )
}

const ResubmitContainer = styled.div<{ hasResubmitted?: boolean }>`
    display: flex;
    gap: ${({ theme }) => theme.spacing[300]};
    align-items: baseline;
    margin-top: 14px;
    ${({ hasResubmitted }) => (hasResubmitted ? { height: 20 } : undefined)}
`

export default styled(ProgressBlock)`
    margin-top: auto;
    display: flex;
    flex-direction: column;
    flex: 1;
    justify-content: flex-end;

    .comment-and-vote-summary {
        justify-content: space-between;
    }
`
